/**
 * The waiter's work in the Tafo app (prototype ob*, blok J), as pure functions: where an order belongs
 * (a table or the takeaway), the equal shares and the tip, what one guest pays per product, the bank-QR
 * and what the app says about the printers. The screens only render; the server computes every price.
 */
import type { Channel, FloorRoom, LineRequest, Order, PrintNotice } from "../../api/types";
import type { Cents } from "../../lib/money";
import type { DraftLine } from "../app-storage";

/** A table of the floor plan, or the takeaway of this app (prototype ctx {kind: "tbl" | "afhaal"}). */
export type Spot = { kind: "table"; table: string } | { kind: "takeaway" };

/** The key of a spot's draft (prototype obKey): "t:7", or "afhaal". */
export function spotKey(spot: Spot): string {
  return spot.kind === "table" ? `t:${spot.table}` : "afhaal";
}

export function channelOf(spot: Spot): Channel {
  return spot.kind === "table" ? "table" : "takeaway";
}

/** The most of one product on one line, as on the till. */
export const MAX_QUANTITY = 999;
/** A guest at the table (QR-bestellen) orders at most this many of one line; the server refuses more. */
export const MAX_GUEST_QUANTITY = 50;

/** A line without anything of its own: no choices, nothing left out, no note, not free. */
const plain = (line: LineRequest) =>
  Object.keys(line.choices ?? {}).length === 0 &&
  (line.without ?? []).length === 0 &&
  (line.only ?? []).length === 0 &&
  (line.note ?? "") === "" &&
  line.free !== true &&
  line.promotion !== true;

/**
 * A product put on the draft (Selim, 18/10/2026: "2 cola's aanduiden gaat niet"): the same plain product in
 * the same size counts up on the line that is there; anything with its own choices gets a line of its own.
 */
export function addToDraft(
  lines: readonly DraftLine[],
  line: LineRequest,
  key: string,
  max = MAX_QUANTITY,
): DraftLine[] {
  if (plain(line)) {
    const index = lines.findIndex(
      (other) => plain(other) && other.productId === line.productId && other.variant === line.variant,
    );
    if (index >= 0) {
      return lines.map((other, at) =>
        at === index ? { ...other, quantity: Math.min(max, other.quantity + line.quantity) } : other,
      );
    }
  }
  return [...lines, { ...line, key }];
}

/** − and + on a line not sent yet: never below one (× takes the line off); the free product of an action stays one. */
export function changeQuantity(lines: readonly DraftLine[], key: string, delta: number): DraftLine[] {
  return lines.map((line) =>
    line.key !== key || line.promotion === true
      ? line
      : { ...line, quantity: Math.max(1, Math.min(MAX_QUANTITY, line.quantity + delta)) },
  );
}

/** The open order per table, as the tables screen shows it. */
export function openTablesOf(orders: readonly Order[]): Map<string, Order> {
  const open = new Map<string, Order>();
  for (const order of orders) {
    if (order.channel === "table" && order.status === "open" && order.table !== null) open.set(order.table, order);
  }
  return open;
}

/** The tables of each room, in the order of the floor plan (prototype TABLE_ZONES). */
export function tablesByRoom(floor: readonly FloorRoom[]): { room: FloorRoom; tables: string[] }[] {
  return [...floor]
    .sort((a, b) => a.position - b.position)
    .map((room) => ({
      room,
      tables: room.items.flatMap((item) => (item.kind === "table" && item.label !== null ? [item.label] : [])),
    }))
    .filter(({ tables }) => tables.length > 0);
}

/** Round half up, as the server does with cents. */
const divideRoundHalfUp = (numerator: number, denominator: number) =>
  Math.floor((2 * numerator + denominator) / (2 * denominator));

/**
 * "Gelijk verdelen" (prototype obPayHtml): the share of a person, as the server checks it: the total
 * divided by the number of persons, rounded to the cent; the last person pays what is left.
 */
export function equalShare(totalCents: Cents, paidCents: Cents, persons: number, person: number): Cents {
  const due = totalCents - paidCents;
  return person >= persons ? due : Math.min(due, divideRoundHalfUp(totalCents, persons));
}

/** The tip on top of an amount: 0, 5, 10 or 15% (prototype p.tipA), rounded to the cent. */
export function tipOf(amountCents: Cents, percent: number): Cents {
  return divideRoundHalfUp(amountCents * percent, 100);
}

export const TIP_PERCENTS = [0, 5, 10, 15] as const;

/** What one guest pays "per product": the units he picked of each line (a free line costs nothing). */
export function pickedAmount(order: Pick<Order, "lines">, picked: ReadonlyMap<number, number>): Cents {
  return order.lines.reduce(
    (total, line, index) => total + (line.free ? 0 : line.unitCents * (picked.get(index) ?? 0)),
    0,
  );
}

/** Every unit of every line picked: that is simply paying the whole table ("Alles"). */
export function pickedEverything(order: Pick<Order, "lines">, picked: ReadonlyMap<number, number>): boolean {
  return order.lines.every((line, index) => (picked.get(index) ?? 0) >= line.quantity);
}

/**
 * One guest of the verdeling on the bill (Furkan, 09/10/2026): what he had, by what the lines are (so a
 * line added or paid meanwhile does not mix up the guests), and whether he wants his own QR on the bill.
 */
export interface Guest {
  /** "Gast 2": his number on the bill, kept when others are taken off or have paid. */
  number: number;
  picks: { key: string; quantity: number }[];
  qr: boolean;
}

/** The number for the next guest on the bill. */
export const nextGuestNumber = (guests: readonly Guest[]) => Math.max(0, ...guests.map((guest) => guest.number)) + 1;

/** A guest of the bill as it stands on the table now: his lines, gone, or paid with his own QR. */
export type GuestOnTable = Map<number, number> | null | "paid";

/** What makes two lines of a table the same thing at the same price. */
export function lineKeyOf(line: Order["lines"][number]): string {
  return JSON.stringify([line.productId, line.variant, line.unitCents, line.free, line.note, line.modifiers]);
}

/** A guest from the units picked per line of the table. */
export function guestOf(
  order: Pick<Order, "lines">,
  picked: ReadonlyMap<number, number>,
  qr: boolean,
  number: number,
): Guest {
  return {
    number,
    picks: [...picked]
      .filter(([, quantity]) => quantity > 0)
      .map(([index, quantity]) => ({ key: lineKeyOf(order.lines[index]!), quantity })),
    qr,
  };
}

/**
 * The guests' picks as lines of the table as it is now, each unit given once. A guest who paid with his own
 * QR is "paid" (his lines became his own order); one whose lines are no longer all on the table (paid
 * another way, taken away) has none: null in his place.
 */
export function guestsOnTable(
  order: Pick<Order, "lines"> & { paidGuests?: number[] },
  guests: readonly Guest[],
): GuestOnTable[] {
  const used = new Map<number, number>();
  const paid = new Set(order.paidGuests ?? []);
  return guests.map((guest): GuestOnTable => {
    if (paid.has(guest.number)) return "paid";
    const picked = new Map<number, number>();
    for (const { key, quantity } of guest.picks) {
      let left = quantity;
      for (const [index, line] of order.lines.entries()) {
        if (left === 0) break;
        if (lineKeyOf(line) !== key) continue;
        const take = Math.min(line.quantity - (used.get(index) ?? 0), left);
        if (take <= 0) continue;
        used.set(index, (used.get(index) ?? 0) + take);
        picked.set(index, (picked.get(index) ?? 0) + take);
        left -= take;
      }
      if (left > 0) return null;
    }
    return picked;
  });
}

/** How many units of each line the guests on the bill already have: the rest can still be picked. */
export function assignedUnits(maps: readonly GuestOnTable[]): Map<number, number> {
  const total = new Map<number, number>();
  for (const map of maps) {
    if (map === null || map === "paid") continue;
    for (const [index, quantity] of map) total.set(index, (total.get(index) ?? 0) + quantity);
  }
  return total;
}

/**
 * The bank-QR of a shop without a key of Bancontact Pro (EPC QR code, "SEPA credit transfer"): a bank app
 * reads the shop's account, the amount and a reference (prototype: "BCD 002 1 SCT … EUR18.32 … Tafo
 * tafel"), and the waiter or the courier confirms the payment himself. With a key the QR is Bancontact's
 * and confirms itself (BankQrPanel, ADR 0024).
 */
export function epcQr(name: string, iban: string, amountCents: Cents, reference: string): string {
  const euros = `EUR${(amountCents / 100).toFixed(2)}`;
  return [
    "BCD",
    "002",
    "1",
    "SCT",
    "",
    name.slice(0, 70),
    iban.replace(/\s+/g, ""),
    euros,
    "",
    "",
    reference.slice(0, 140),
  ].join("\n");
}

/** The computer that prints for the app (a USB printer at Kassa PC 1), or null for the network. */
export function printingPc(notices: readonly PrintNotice[] | undefined): string | null {
  return notices?.find((notice) => notice.via != null)?.via ?? null;
}

/** The kitchen printers that got something, in order, each once ("Pizza-printer, Kassaprinter toog"). */
export function kitchenPrinters(notices: readonly PrintNotice[] | undefined): string[] {
  const kitchen = (notices ?? []).filter((notice) => notice.kind === "kitchen" || notice.kind === "copy");
  return [...new Set(kitchen.map((notice) => notice.printer))];
}

/** The printer of the receipt or the bill, when one was printed. */
export function receiptPrinter(
  notices: readonly PrintNotice[] | undefined,
  kind: "receipt" | "bill" = "receipt",
): PrintNotice | null {
  return notices?.find((notice) => notice.kind === kind) ?? null;
}

/** "Kopie van de afrekening" in the kitchen (Werkwijze keuken): the prints say so. */
export function kitchenCopies(notices: readonly PrintNotice[] | undefined): boolean {
  return (notices ?? []).some((notice) => notice.kind === "copy");
}
